import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useLanguage } from '../functions/useLanguage';

/**
 * A dismissible banner shown when the user's trial ends in ≤ 3 days.
 * The hard gate (HardGate.tsx) takes over once the trial has actually expired.
 */
export function TrialBanner() {
  const { phase, trialDaysLeft } = useTrialStatus();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Dismiss key changes each day so the banner re-appears on a new day
  const today = new Date().toISOString().split('T')[0];
  const [dismissedDay, setDismissedDay] = useState<string | null>(null);

  // Only show during the warning phase (trial ending ≤ 3 days away)
  if (phase !== 'warning') return null;
  if (dismissedDay === today) return null;

  const isLastDay = trialDaysLeft <= 1;

  return (
    <div
      className={`w-full px-4 py-2.5 flex items-center justify-between gap-3 text-sm font-medium z-40 ${
        isLastDay
          ? 'bg-error-red text-white'
          : 'bg-amber-400 text-amber-900'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="truncate">
          {isLastDay
            ? t('trial.endsToday')
            : t('trial.endsIn')
              .replace('{days}', String(trialDaysLeft))
              .replace('{s}', trialDaysLeft === 1 ? '' : 's')}
        </span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => navigate('/website')}
          className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
            isLastDay
              ? 'border-white text-white hover:bg-error-red'
              : 'border-amber-700 text-amber-900 hover:bg-amber-500'
          }`}
        >
          {t('trial.upgrade')}
        </button>
        <button
          aria-label={t('trial.dismiss')}
          onClick={() => setDismissedDay(today)}
          className="p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
